import React, { useCallback, useSyncExternalStore } from 'react'
import { useTranslations } from 'next-intl'
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/ssr/ArrowClockwise'
import { ArrowCounterClockwiseIcon } from '@phosphor-icons/react/dist/ssr/ArrowCounterClockwise'
import { Button } from '@skop-app/lib-front'
import { cx } from 'class-variance-authority'

import { useGridApi, useGridViews } from './GridRoot'
import { GRID_TOOLBAR_CONTROL_CLASSNAME } from './toolbarControlStyles'
import { usePreventGridFocusSteal } from './usePreventGridFocusSteal'
import type { GridUndoRedoProps } from '../types'

const NOOP_UNSUBSCRIBE = () => {}
const UNDO_REDO_EVENTS = [
  'cellValueChanged',
  'cellEditingStopped',
  'undoEnded',
  'redoEnded',
  'modelUpdated',
  'pasteEnd',
  'fillEnd',
  'batchEditingStopped',
  'bulkEditingStopped',
] as const

export function GridUndoRedo({
  className,
  undoTitle,
  redoTitle,
  disabled,
}: GridUndoRedoProps) {
  const t = useTranslations()
  const { api, gridIsReady } = useGridApi()
  const views = useGridViews()
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!api || !gridIsReady) return NOOP_UNSUBSCRIBE

      for (const eventName of UNDO_REDO_EVENTS) {
        api.addEventListener(eventName, onStoreChange)
      }

      return () => {
        try {
          for (const eventName of UNDO_REDO_EVENTS) {
            api.removeEventListener(eventName, onStoreChange)
          }
        } catch {
          // grid may be torn down during fast navigation
        }
      }
    },
    [api, gridIsReady],
  )
  const getCanUndo = useCallback(
    () => (api && gridIsReady ? api.getCurrentUndoSize() > 0 : false),
    [api, gridIsReady],
  )
  const getCanRedo = useCallback(
    () => (api && gridIsReady ? api.getCurrentRedoSize() > 0 : false),
    [api, gridIsReady],
  )
  const canUndo = useSyncExternalStore(subscribe, getCanUndo, () => false)
  const canRedo = useSyncExternalStore(subscribe, getCanRedo, () => false)

  // Prevent mousedown from stealing focus away from the grid.
  // Without this, stopEditingWhenCellsLoseFocus commits the active editor
  // before the click handler runs, which re-triggers the last mutation and
  // clears the redo stack.
  const preventFocusSteal = usePreventGridFocusSteal()
  const handleUndo = useCallback(() => {
    api?.undoCellEditing()
  }, [api])
  const handleRedo = useCallback(() => {
    api?.redoCellEditing()
  }, [api])

  const resolvedUndoTitle = undoTitle ?? t('Toolbar.undo')
  const resolvedRedoTitle = redoTitle ?? t('Toolbar.redo')
  const isDisabled =
    views?.isInteractionBlocked === true || (disabled ?? !gridIsReady)

  return (
    <div className={cx('flex gap-2', className)}>
      <Button
        variant="action"
        onMouseDown={preventFocusSteal}
        onClick={handleUndo}
        disabled={isDisabled || !canUndo}
        title={resolvedUndoTitle}
        className={cx(
          GRID_TOOLBAR_CONTROL_CLASSNAME,
          'flex items-center gap-1',
        )}
      >
        <ArrowCounterClockwiseIcon className="size-4" />
      </Button>
      <Button
        variant="action"
        onMouseDown={preventFocusSteal}
        onClick={handleRedo}
        disabled={isDisabled || !canRedo}
        title={resolvedRedoTitle}
        className={cx(
          GRID_TOOLBAR_CONTROL_CLASSNAME,
          'flex items-center gap-1',
        )}
      >
        <ArrowClockwiseIcon className="size-4" />
      </Button>
    </div>
  )
}
